
import * as React from "react";
import { findAcronym, isKnownAcronym, ProcurementAcronym } from "@/utils/procurementAcronyms";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AcronymTooltipProps {
  acronym: string;
  displayIcon?: boolean;
  children?: React.ReactNode;
}

export const AcronymTooltip: React.FC<AcronymTooltipProps> = ({
  acronym,
  displayIcon = true,
  children
}) => {
  const acronymData = findAcronym(acronym);
  
  if (!acronymData) {
    return <>{children || acronym}</>;
  }
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center cursor-help border-b border-dotted border-gray-400">
            {children || acronym}
            {displayIcon && <Info className="h-3.5 w-3.5 ml-0.5 text-gray-500" />}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm p-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="font-bold">{acronymData.acronym}</Badge>
              <span className="font-medium text-sm">{acronymData.fullName}</span>
            </div>
            <p className="text-sm">{acronymData.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface AutoAcronymDetectorProps {
  text: string;
  highlightStyle?: "underline" | "badge" | "icon";
}

export const AutoAcronymDetector: React.FC<AutoAcronymDetectorProps> = ({
  text,
  highlightStyle = "underline"
}) => {
  if (!text) return null;
  
  const words = text.split(/(\s+)/);
  
  return (
    <>
      {words.map((word, index) => {
        // Clean word from punctuation for checking
        const cleanWord = word.replace(/[.,;:!?()\[\]{}'"]/g, '');
        
        if (isKnownAcronym(cleanWord)) {
          const punctuation = word.match(/([.,;:!?()\[\]{}'"]+)$/)?.[1] || '';
          const acronymOnly = word.replace(/[.,;:!?()\[\]{}'"]/g, '');
          
          return (
            <React.Fragment key={index}>
              <AcronymTooltip acronym={cleanWord} displayIcon={highlightStyle === "icon"}>
                {highlightStyle === "badge" ? (
                  <Badge variant="outline" className="font-normal text-xs">
                    {acronymOnly}
                  </Badge>
                ) : (
                  acronymOnly
                )}
              </AcronymTooltip>
              {punctuation}
              {word.match(/\s+/) ? word : ''}
            </React.Fragment>
          );
        }
        
        return <React.Fragment key={index}>{word}</React.Fragment>;
      })}
    </>
  );
};
